import { Form, useForm } from "@/hooks/form.hook";
import { SelectElement } from "@/components/formElements/select-element";
import { SelectOption } from "@/types/SelectOption";
import { useOrganisationService } from "@/hooks/organisation.hooks";
import { useToast } from "@/hooks/toast.hooks";
import { XCircleIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CANCELLATION_STATISTICS,
  LITE_CANCELLED_STATISTICS,
  LITE_DASHBOARD_STATISTICS,
  LITE_PRODUCT_SKU_STATISTICS,
  LITE_PRODUCT_STATISTICS,
} from "@/hooks/tiktokOrder.hooks";
import { LITE_SETTLED_DASHBOARD_STATISTICS } from "@/hooks/tiktokSkuSettledPeriod.hooks";
import { ORGANISATION_PLAN } from "@/hooks/organisation.hooks";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { useContext, useEffect } from "react";

const applyToOptions: SelectOption[] = [
  { value: "UNIT", label: "Apply to each unit" },
  { value: "ORDER", label: "Apply to each order" },
];

export const ShippingApplyTo = ({ onChange }: { onChange?: () => void }) => {
  const form = useForm({
    fields: {
      applyTo: "UNIT",
    },
  });
  const { shippingApplyTo } = useContext(UserStateContext);
  const { updateSettings } = useOrganisationService();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleUpdateSettings = async () => {
    const response = await updateSettings({
      shippingApplyTo: form.state.applyTo,
    });
    if (!response.success) {
      toast({
        icon: <XCircleIcon size={12} className={"text-red-500"} />,
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: response.errorMessage!,
      });
      return;
    }
    queryClient.invalidateQueries({
      queryKey: [LITE_DASHBOARD_STATISTICS],
    });
    queryClient.invalidateQueries({
      queryKey: [LITE_SETTLED_DASHBOARD_STATISTICS],
    });
    queryClient.invalidateQueries({
      queryKey: [LITE_PRODUCT_STATISTICS],
    });
    queryClient.invalidateQueries({
      queryKey: [LITE_PRODUCT_SKU_STATISTICS],
    });
    queryClient.invalidateQueries({
      queryKey: [LITE_CANCELLED_STATISTICS],
    });
    queryClient.invalidateQueries({
      queryKey: [CANCELLATION_STATISTICS],
    });
    queryClient.invalidateQueries({
      queryKey: [ORGANISATION_PLAN],
    });
  };

  const handleChange = async () => {
    await handleUpdateSettings();
  };

  useEffect(() => {
    form.setState({
      applyTo: shippingApplyTo,
    });
  }, [shippingApplyTo]);

  return (
    <SelectElement
      fieldName="applyTo"
      form={form}
      options={applyToOptions}
      helper="Apply shipping costs to each individual unit or each order. This will affect how shipping is calculated for seller shipped orders."
      onChange={handleChange}
    />
  );
};
